// Split out of src/components/Tests.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Tests.jsx
// re-exports everything it exported before, so existing imports keep working.

import { DoubledTokens, Celebration, Icon } from "../Common.jsx";
import { useGameTelemetry } from "../../lib/metrics.js";
import { oinkSessionOP } from "../../lib/oinks.js";
import { generateAdaptiveMathsSession, generateMathsSession, MATHS_BANDS, withAnswerPrompt } from "../../lib/maths.js";
import { levelWord } from "../../lib/adaptive.js";
import { t as ct } from "../../lib/content.js";
import { playError, deferToEnd, answersMatch, playDing, OinkIntroLine, TestOverlay, scoreEmoji, TokenRewardRow, OinkRewardRow } from "./shared.jsx";
import { NumericKeypad } from "./keyboards.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function MathsWorkoutTest({ doubleDay = false, band, testSize, passThreshold, passReward, fullReward, timerSeconds, onLearnEarn, adaptiveState, adaptiveParamsValue, onItemResults, payoutsPerWeek, payGate, payoutsUsed, kidTheme, weeklyCapMessage, oinksOn, onEarnOinks, partyEmoji, onSessionFinished }) {
  const mwResultsRef = useRef([]);
  // LB-366: what was typed, for the test history (display text; the engine gets mwResultsRef).
  const mwHistoryRef = useRef([]);
  const mwQuestionRef = useRef(null); // the question on screen, for the timer effect below
  // Questions parked once already — a second skip is final (see deferToEnd).
  const mwDeferredRef = useRef(new Set());
  const [sessionKey, setSessionKey] = useState(0);
  const [idx, setIdx] = useState(0);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [phase, setPhase] = useState("intro");
  // W2.5 Learn & Earn funnel: game_start / game_complete / game_abandon ride the
  // shared phase shape (intro → testing → finished). Age band is stamped by the
  // metrics module (App.jsx keeps it current), so no child prop is needed here.
  useGameTelemetry("mathsworkout", phase);
  const [awarded, setAwarded] = useState(false);
  // Set when onLearnEarn routes this pass's reward to the parent instead of crediting it
  // instantly (spending cap breached/blocked) — holds the kid-friendly message to show in
  // place of the usual reward celebration.
  const [capNotice, setCapNotice] = useState(null);
  const [timeLeft, setTimeLeft] = useState(timerSeconds || 0);
  const advanceRef = useRef(null);
  const tickRef = useRef(null);

  const perWeek = payoutsPerWeek || 1;
  // 12 Sep (Alan): on Coach and By-Year lists a game pays once at the PASS tier and once at
  // FULL marks, so scoring 80% no longer ends the week — going back for 100% still pays,
  // which is the behaviour worth encouraging. Custom lists keep the plain weekly count,
  // because replaying a parent's own short word list IS farmable. payGate falls back to the
  // old rule when a caller has not supplied one.
  const gate = payGate || { full: (payoutsUsed || 0) < perWeek, pass: (payoutsUsed || 0) < perWeek };
  const onCooldown = !gate.full && !gate.pass;
  // Snapshot of onCooldown taken the moment this test finishes, then frozen. Without this,
  // the results screen has a real bug: crediting THIS session's reward updates payoutsUsed
  // via the parent's state, which re-renders this component with onCooldown now true —
  // since the very payout being reported is what just pushed the weekly count to its limit
  // — so the "you earned it" card flips to "you're already on cooldown" the instant the
  // reward that just earned it lands (staging feedback, Aug 2026). Freezing the value the
  // moment phase becomes "finished" keeps the results screen honest about the session it's
  // actually reporting on, rather than about state that session's own payout just changed.
  const onCooldownAtFinishRef = useRef(onCooldown);
  if (phase !== "finished") onCooldownAtFinishRef.current = onCooldown;
  // Drawn once per run, in state — same fix and same reason as the three tests above.
  const [session, setSession] = useState([]);
  const question = session[idx]; mwQuestionRef.current = question;
  const bandInfo = MATHS_BANDS[band] || MATHS_BANDS.builder;
  const timerOn = (timerSeconds || 0) > 0;

  useEffect(() => {
    if (phase === "finished" && !awarded) {
      setAwarded(true);
      const pct = session.length > 0 ? (correctCount / session.length) * 100 : 0;
      // Oinks first: paid on every completed test regardless of cooldown, cap, or score.
      const sessionOinks = oinksOn && onEarnOinks ? oinkSessionOP(correctCount, pct >= 100).op : 0;
      if (oinksOn && onEarnOinks) onEarnOinks(sessionOinks);
      if (onSessionFinished) onSessionFinished("mathsworkout", correctCount, session.length, pct >= 100);
      const reward = pct >= 100 ? fullReward : pct >= passThreshold ? passReward : 0;
      const tier = pct >= 100 ? "full" : "pass";
      const history = { questions: mwHistoryRef.current.slice(), oinks: sessionOinks, qualified: reward > 0 ? tier : null, gateOpen: reward > 0 && !!gate[tier], passMark: passThreshold, passKind: "pct" };
      if (onItemResults) onItemResults("mathsworkout", mwResultsRef.current.slice(), false, { total: session.length, history });
      if (reward > 0 && gate[tier]) {
        const result = onLearnEarn(reward, `Maths Workout (${bandInfo.label}): ${correctCount}/${session.length} correct (${Math.round(pct)}%)`, "mathsworkout", tier);
        if (result && !result.credited) setCapNotice(result.capMessage || null);
      }
    }
  }, [phase]);

  // Countdown per question — starts fresh each time a new question appears, only while
  // actually testing and only when a timer is configured at all.
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (phase !== "testing" || !timerOn) return;
    setTimeLeft(timerSeconds);
    tickRef.current = setInterval(() => {
      setTimeLeft((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, sessionKey, phase, timerOn]);

  // Hitting zero with no answer submitted yet counts the question wrong and moves on — same
  // auto-advance path a real answer takes, just triggered by the clock instead of a tap.
  useEffect(() => {
    if (phase === "testing" && timerOn && timeLeft === 0 && result === null) {
      // Alan, 23 Sep: a question the timer runs out on counts as a MISS for Learning Coach.
      // Before this it reached the engine as nothing at all, so a child who let the clock
      // run out on the hard ones looked, to Coach, as if they had only ever been asked the
      // easy ones. Same row shape as an answered miss.
      const q = mwQuestionRef.current;
      if (q) {
        mwResultsRef.current.push({ id: q.prompt, grade: q.grade, topic: q.topic, level: q.level, correct: false });
        mwHistoryRef.current.push({ q: q.prompt, given: "", answer: q.answer, correct: false, timedOut: true });
      }
      playError();
      setResult("timeout");
      advanceRef.current = setTimeout(() => { advance(); }, 900);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  useEffect(() => () => { if (advanceRef.current) clearTimeout(advanceRef.current); if (tickRef.current) clearInterval(tickRef.current); }, []);

  function startTest() { mwResultsRef.current = []; mwHistoryRef.current = []; mwDeferredRef.current = new Set(); setSession(adaptiveState ? generateAdaptiveMathsSession(band, testSize, adaptiveState, adaptiveParamsValue) : generateMathsSession(band, testSize)); setSessionKey((k) => k + 1); setIdx(0); setInput(""); setResult(null); setCorrectCount(0); setAwarded(false); setCapNotice(null); setPhase("testing"); }
  function restart() { setPhase("intro"); }
  // 12 Sep: as above — no blank submissions, and Skip scores as wrong.
  function check(skipped = false) {
    if (result !== null) return;
    if (!skipped && !String(input).trim()) return;
    // Same rule as spelling and vocabulary: park it, meet it again before the end (see
    // deferToEnd). Tables deliberately do NOT do this — a streak game must not let a child
    // skip past the hard one for free.
    if (skipped && !mwDeferredRef.current.has(question.prompt)) {
      const reordered = deferToEnd(session, idx);
      if (reordered) {
        mwDeferredRef.current.add(question.prompt);
        setSession(reordered);
        setInput("");
        setResult(null);
        return;
      }
    }
    if (tickRef.current) clearInterval(tickRef.current);
    const correct = !skipped && answersMatch(input, question.answer);
    mwResultsRef.current.push({ id: question.prompt, grade: question.grade, topic: question.topic, level: question.level, correct });
    mwHistoryRef.current.push({ q: question.prompt, given: skipped ? "" : String(input).trim(), answer: question.answer, correct, skipped: !!skipped });
    setResult(correct ? "correct" : "wrong");
    if (correct) { playDing(); setCorrectCount((c) => c + 1); } else { playError(); }
    advanceRef.current = setTimeout(() => { advance(); }, 700);
  }
  function advance() {
    setIdx((i) => {
      if (i + 1 >= session.length) { setPhase("finished"); return i; }
      setInput(""); setResult(null);
      return i + 1;
    });
  }

  if (phase === "intro") {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
        <div className="mb-2 flex justify-center"><Icon id="ic-maths" size={36} hue="indigo" /></div>
        <div className="font-semibold text-slate-700 text-sm mb-1">{testSize} questions · {bandInfo.label}{adaptiveState ? ` · ${levelWord(adaptiveState.target)}` : ""}</div>
        <p className="text-xs text-slate-400 mb-2">{bandInfo.blurb}</p>
        <p className="text-base text-slate-500 mb-4">
          {weeklyCapMessage
            ? weeklyCapMessage
            : onCooldown
            ? `Practice only — you've already earned ${perWeek > 1 ? `all ${perWeek} rewards` : "this week's reward"} for maths, resets Monday.`
            : doubleDay
            ? <>Score 100% to earn <DoubledTokens plus={false} base={fullReward} /> tokens, or {passThreshold}%+ for <DoubledTokens plus={false} base={passReward} />. <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">{ct("tests.double_token_chip", "Double token day")}</span></>
            : `Score 100% to earn ${fullReward} tokens, or ${passThreshold}%+ for ${passReward}.`}
        </p>
        {oinksOn && <OinkIntroLine moneyPaused={!!weeklyCapMessage || onCooldown} />}
        <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={startTest}>{ct("tests.start_button", "Start test")}</button>
      </div>
    );
  }

  const timerColor = timeLeft <= 1 ? "text-rose-600 bg-rose-50" : timeLeft <= 3 ? "text-amber-600 bg-amber-50" : "text-emerald-600 bg-emerald-50";

  return (
    <TestOverlay onClose={restart} focused={phase === "testing"}>
      {phase === "finished" ? (() => {
        const pct = Math.round((correctCount / session.length) * 100);
        const reward = pct >= 100 ? fullReward : pct >= passThreshold ? passReward : 0;
        const rewardEarned = reward > 0 && !onCooldownAtFinishRef.current && !capNotice;
        const oink = oinkSessionOP(correctCount, pct >= 100);
        return (
          <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
            {/* Was gated on pct >= 100 — a pass-mark score genuinely earns tokens too (see the
                🎉 messaging right below, which already celebrates it), so withholding the
                confetti made "you earned tokens" and "you get a celebration" inconsistent. */}
            {rewardEarned && <Celebration variant="pig" emojiOverride={partyEmoji} />}
            <div className={`font-extrabold text-3xl mb-1 ${rewardEarned ? "text-emerald-500" : "text-slate-700"}`}>{scoreEmoji(pct)} {correctCount}/{session.length} <span className="text-lg text-slate-400">({pct}%)</span></div>
            {capNotice ? (
              <div className="text-amber-600 font-bold text-base mb-4">{capNotice}</div>
            ) : rewardEarned ? (
              <TokenRewardRow amount={reward} detail={`${correctCount}/${session.length} correct (${pct}%)${reward === fullReward ? " — full marks" : ""}`} />
            ) : reward > 0 ? (
              <div className="text-slate-500 text-base mb-4">{reward === fullReward ? "Full marks" : `${passThreshold}%+ correct`} You've already earned {perWeek > 1 ? "all this week's maths rewards" : "this week's maths reward"} — resets Monday.</div>
            ) : (
              <div className="text-slate-500 text-base mb-4">Score {passThreshold}% or higher to earn tokens.</div>
            )}
            {oinksOn && <OinkRewardRow op={oink.op} detail={oink.detail} />}
            <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm`} onClick={restart}>{reward > 0 ? "Back to Learn & Earn" : "Try again"}</button>
          </div>
        );
      })() : (
        <div key={`q-${sessionKey}-${idx}`} className="bg-white rounded-2xl px-3 py-5 shadow-sm border border-slate-100 text-center">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-slate-400 font-medium text-left">Q{idx + 1}/{session.length} · {onCooldown ? "practice only — reward resets Monday" : `${passThreshold}%+ earns ${passReward}, 100% earns ${fullReward}`}</div>
            <button className="lb-inline-quit text-xs text-slate-400 font-semibold flex-shrink-0 ml-2" onClick={restart}>{ct("tests.quit_restart", "Quit / Restart")}</button>
          </div>
          {timerOn && (
            <div className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-sm font-semibold mb-3 ${timerColor}`}>⏱ {timeLeft}s</div>
          )}
          <div className="font-extrabold text-2xl text-slate-800 mb-4">{withAnswerPrompt(question.prompt)}</div>
          <NumericKeypad value={input} disabled={result !== null} allowDecimal allowNegative onEnter={() => check()}
            onDigit={(d) => setInput((v) => (v.length < 8 ? v + d : v))}
            onBackspace={() => setInput((v) => v.slice(0, -1))}
            onDecimal={() => setInput((v) => (v.includes(".") ? v : v + "."))}
            onToggleSign={() => setInput((v) => (v.startsWith("-") ? v.slice(1) : `-${v}`))} />
          {result === null ? (
            <>
              <button disabled={!String(input).trim()} className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-base w-full mt-3 disabled:opacity-40`} onClick={() => check()}>{ct("tests.check_button", "Check")}</button>
              <button className="w-full mt-2 text-sm font-bold text-slate-500 py-2" data-testid="test-skip" onClick={() => check(true)}>{(question && mwDeferredRef.current.has(question.prompt)) || idx >= session.length - 1 ? ct("tests.skip_question", "Skip this question") : ct("tests.skip_for_now", "Skip for now")}</button>
            </>
          ) : (
            <div className={`font-bold text-base mt-3 flex items-center justify-center gap-1.5 ${result === "correct" ? "text-emerald-500" : "text-rose-500"}`}>
              <Icon id={result === "timeout" ? "ic-attention" : result === "correct" ? "ic-celebrate" : "ic-close"} size={18} hue={result === "timeout" ? "amber" : result === "correct" ? "pink" : "rose"} />
              {result === "timeout" ? `Time's up. It was ${question.answer}` : result === "correct" ? "Correct" : `It was ${question.answer}`}
            </div>
          )}
        </div>
      )}
    </TestOverlay>
  );
}
